import { Button, Checkbox, Descriptions, Form, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormatDate } from '../../../../constants';
import '../../../../css/entity-style.css';
import { Entity, EntityUpdateInput } from '../../../../generated';
import { Country } from '../../../../types/country';
import StateSelect from '../../../shared/state-select';
import { isZipCodeRequired } from './entity-create';

const { Option } = Select;

interface EntityEditProps {
  onSubmit: (payload: EntityUpdateInput) => void;
  loading: boolean;
  entity: Entity;
  countryData: Country[];
}

const CountrySelect: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  countryData: Country[];
}> = ({ value, onChange, countryData }) => {
  return (
    <Select placeholder="Select a Country" allowClear showSearch optionFilterProp="children" value={value} onChange={onChange}>
      {countryData?.map((item: Country) => (
        <Option key={item.countryCode} value={item.countryCode}>
          {item.countryName}
        </Option>
      ))}
    </Select>
  );
};

interface AddressSectionProps {
  prefix: (string | number)[];
  selectedCountry: string;
  countryData: any;
}

const AddressSection: React.FC<AddressSectionProps> = ({
  prefix,
  selectedCountry,
  countryData,
}) => {
  return (
    <>
    <div style={{ display: 'flex' }}>
      <div className="formBox">
        <Form.Item
          style={{ width: '100%' }}
          label="City"
          name={[...prefix, 'city']}
          rules={[{ required: true, message: 'City is Required' }]}
        >
          <Input />
        </Form.Item>
      </div>
    </div>
    <div style={{ display: 'flex' }}>
      <div className="formBox">
        <StateSelect
          name={[...prefix, 'stateOrProvince']}
          countryData={countryData}
          selectedCountry={selectedCountry}
        />
      </div>
      <div className="formBox">
        <Form.Item
          style={{ width: '100%' }}
          label="Zip Code / Postal Code"
          name={[...prefix, 'zipOrPostalCode']}
          rules={[
            {
              required: isZipCodeRequired(selectedCountry),
              message: 'Zip Code / Postal Code is Required',
            },
          ]}
        >
          <Input />
        </Form.Item>
      </div>
    </div>
    </>
  );
};

export const EntityEdit: React.FC<EntityEditProps> = ({ onSubmit, loading, entity, countryData }) => {
  const [form] = Form.useForm<EntityUpdateInput>();

  const [isClientToggle, setIsClientToggle] = useState<boolean>(false);
  const [isIssuingInstitution, setIsIssuingInstitution] = useState<boolean>(false);

  useEffect(() => {
    if (entity?.isClient) {
      setIsClientToggle(true);
    }
    if (entity?.isIssuingInstitution) {
      setIsIssuingInstitution(true);
    }
  }, [entity]);

  const handleClientCountryChange = () => {
    form.setFieldsValue({
      client: { address: { stateOrProvince: null, zipOrPostalCode: null } }
    });
  };

  const handleInstitutionCountryChange = () => {
    form.setFieldsValue({
      issuingInstitution: { address: { stateOrProvince: null, zipOrPostalCode: null } }
    });
  };

  const selectedCountryClient = Form.useWatch(['client', 'address', 'country'], form);
  const selectedCountryIssuingInstitution = Form.useWatch(['issuingInstitution', 'address', 'country'], form);

  const metadataItems = [
    { label: 'Id', value: entity?.id ?? '' },
    { label: 'Created At', value: FormatDate(entity?.createdAt) },
    entity?.updatedAt && {
      label: 'Updated At',
      value: FormatDate(entity?.updatedAt)
    }
  ].filter(Boolean);

  return (
    <div className="container">
        <Descriptions layout="vertical" column={metadataItems.length} className="mb-10">
          {metadataItems.map((item) => (
            <Descriptions.Item key={item.label} label={item.label}>
              {item.value}
            </Descriptions.Item>
          ))}
        </Descriptions>

        <Form
          form={form}
          initialValues={entity}
          onFinish={(v) => {
            onSubmit({ ...v, id: entity?.id });
          }}
          layout="vertical"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'right',
            justifyContent: 'end'
          }}
        >
          <div className="formBox">
            <Form.Item label="Entity Name" style={{ width: '100%' }} rules={[{ required: true, message: 'Entity Name is Required' }]} name={'entityName'}>
              <Input />
            </Form.Item>
          </div>

          <div className="formBox">
            <Form.Item label="Country" style={{ width: '100%' }} name={['address', 'country']} rules={[{ required: true, message: 'Country is Required' }]}>
              <CountrySelect countryData={countryData} />
            </Form.Item>
          </div>

          <div className="formBox">
            <Form.Item label="City" style={{ width: '100%' }} name={['address', 'city']} rules={[{ required: true, message: 'City is Required' }]}>
              <Input />
            </Form.Item>
          </div>

          <div className="checkBoxContainer">
            <div className="formBox">
              <Form.Item name={'isClient'} valuePropName="checked">
                <Checkbox
                  onClick={() => {
                    setIsClientToggle((prev) => !prev);
                  }}
                >
                  <p style={{ paddingLeft: '10px' }}> Is Client</p>
                </Checkbox>
              </Form.Item>
            </div>

            {isClientToggle ? (
              <div className="subItemContainer">
                <div className="sectionHeading">
                  <p>Client Details</p>
                </div>
                <div className="formBox">
                  <Form.Item
                    style={{ width: '100%' }}
                    label="Address Line 1"
                    name={['client', 'address', 'addressLine1']}
                    rules={[{ required: true, message: 'Address Line 1 is Required' }]}
                  >
                    <Input />
                  </Form.Item>
                </div>

                <div className="formBox">
                  <Form.Item style={{ width: '100%' }} label="Address Line 2" name={['client', 'address', 'addressLine2']}>
                    <Input />
                  </Form.Item>
                </div>

                <div className="formBox">
                  <Form.Item style={{ width: '100%' }} label="Country" name={['client', 'address', 'country']} rules={[{ required: true, message: 'Country is Required' }]}>
                    <CountrySelect
                      countryData={countryData}
                      onChange={() => {
                        handleClientCountryChange();
                      }}
                    />
                  </Form.Item>
                </div>

                <AddressSection
                  prefix={['client', 'address']}
                  selectedCountry={selectedCountryClient}
                  countryData={countryData}
                />

                <div
                  style={{
                    display: 'flex'
                  }}
                >
                  <div className="formBox">
                    <Form.Item
                      style={{ width: '100%' }}
                      label="Email Address"
                      name={['client', 'email']}
                      rules={[
                        {
                          required: true,
                          message: 'Email Address is Required'
                        }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </div>

                  <div className="formBox">
                    <Form.Item
                      style={{ width: '100%' }}
                      label="Phone Number"
                      name={['client', 'phoneNumber']}
                      rules={[{ required: true, message: 'Phone Number is Required' }]}
                    >
                      <Input maxLength={15} />
                    </Form.Item>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="checkBoxContainer">
            <div className="formBox">
              <Form.Item name={'isIssuingInstitution'} valuePropName="checked">
                <Checkbox
                  onClick={() => {
                    setIsIssuingInstitution((prev) => !prev);
                  }}
                >
                  <p style={{ paddingLeft: '10px' }}> Is Issuing Institution</p>
                </Checkbox>
              </Form.Item>
            </div>

            {isIssuingInstitution ? (
              <div className="subItemContainer">
                <div className="sectionHeading">
                  <p>Issuing Institution Details</p>
                </div>
                <div className="formBox">
                  <Form.Item
                    style={{ width: '100%' }}
                    label="Address Line 1"
                    name={['issuingInstitution', 'address', 'addressLine1']}
                    rules={[{ required: true, message: 'Address Line 1 is Required' }]}
                  >
                    <Input />
                  </Form.Item>
                </div>

                <div className="formBox">
                  <Form.Item style={{ width: '100%' }} label="Address Line 2" name={['issuingInstitution', 'address', 'addressLine2']}>
                    <Input />
                  </Form.Item>
                </div>

                <div className="formBox">
                  <Form.Item
                    style={{ width: '100%' }}
                    label="Country"
                    name={['issuingInstitution', 'address', 'country']}
                    rules={[{ required: true, message: 'Country is Required' }]}
                  >
                    <CountrySelect
                      countryData={countryData}
                      onChange={() => {
                        handleInstitutionCountryChange();
                      }}
                    />
                  </Form.Item>
                </div>

                <AddressSection
                  prefix={['issuingInstitution', 'address']}
                  selectedCountry={selectedCountryIssuingInstitution}
                  countryData={countryData}
                />

                <div
                  style={{
                    display: 'flex'
                  }}
                >
                  <div className="formBox">
                    <Form.Item
                      style={{ width: '100%' }}
                      label="Email Address"
                      name={['issuingInstitution', 'email']}
                      rules={[
                        {
                          required: true,
                          message: 'Email Address is Required'
                        }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </div>

                  <div className="formBox">
                    <Form.Item
                      style={{ width: '100%' }}
                      label="Phone Number"
                      name={['issuingInstitution', 'phoneNumber']}
                      rules={[{ required: true, message: 'Phone Number is Required' }]}
                    >
                      <Input maxLength={15} />
                    </Form.Item>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <Form.Item>
            <Button loading={loading} type="primary" htmlType="submit">
              Save Entity
            </Button>
          </Form.Item>
        </Form>
    </div>
  );
};
